import React, { useState, useEffect } from "react";
import Icon from "../../../components/AppIcon";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input"; // تأكد من استيراد الـ Input الخاص بك
import { useDispatch, useSelector } from "react-redux";
import {
  createAdvertismentService,
  getAdvertisementsService,
  updateAdvertisementService,
} from "redux/services/general_service";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

const CampaignModal = ({ isOpen, onClose, campaign }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { isLoadingAction } = useSelector((state) => state.general);

  const [formData, setFormData] = useState({
    image: null,
    imagePreview: null,
    url: "", // إضافة حقل الـ URL
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (campaign) {
      setFormData({
        image: campaign?.image ? campaign.image[0] : null,
        imagePreview: campaign?.image ? campaign.image : null,
        url: campaign?.url || "", // تعبئة الـ URL عند التعديل
      });
    } else {
      setFormData({
        image: null,
        imagePreview: null,
        url: "",
      });
    }
    setErrors({});
  }, [campaign, isOpen]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validTypes = ["image/jpeg", "image/png", "image/gif"];
      const maxSize = 5 * 1024 * 1024;

      if (!validTypes.includes(file.type)) {
        setErrors((prev) => ({ ...prev, image: "Invalid file type." }));
        return;
      }

      if (file.size > maxSize) {
        setErrors((prev) => ({
          ...prev,
          image: "File size must be less than 5MB",
        }));
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prev) => ({
          ...prev,
          image: file,
          imagePreview: { url: reader.result, name: file.name },
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const removeMediaFile = () => {
    setFormData((prev) => ({ ...prev, image: null, imagePreview: null }));
  };

  const handleSave = () => {
    if (!formData?.image) {
      setErrors({ image: "A media file is required" });
      return;
    }

    try {
      const dataToSend = new FormData();
      dataToSend.append("image", formData.image);
      dataToSend.append("url", formData.url); // إرسال الـ URL للسيرفر
      dataToSend.append("type", "ad");

      if (campaign) {
        dataToSend.append("_method", "PUT");
        dispatch(
          updateAdvertisementService({
            id: campaign?.id,
            formData: dataToSend,
          }),
        ).then((response) => {
          if (
            response?.payload?.status === 200 ||
            response?.payload?.status === 201
          ) {
            toast.success("تم التعديل بنجاح !");
            dispatch(getAdvertisementsService({ page: 1, type: "ad" }));
            onClose();
          }
        });
      } else {
        dispatch(createAdvertismentService({ data: dataToSend })).then(
          (response) => {
            if (
              response?.payload?.status === 200 ||
              response?.payload?.status === 201
            ) {
              toast.success("تمت الإضافة بنجاح !");
              dispatch(getAdvertisementsService({ page: 1, type: "ad" }));
              onClose();
            }
          },
        );
      }
    } catch (error) {
      console.error(error);
    }
  };

  if (!isOpen) return null;

  return (
    // الحل لمشكلة السايد بار: استخدام z-[9999] و fixed inset-0
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999] p-4 backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {campaign ? t("editCampaign") : t("createNewCampaign")}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto space-y-6">
          {/* حقل الـ URL الجديد */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              رابط الإعلان (URL)
            </label>
            <input
              type="url"
              className="w-full p-2 border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              placeholder="https://example.com"
              value={formData.url}
              onChange={(e) => handleInputChange("url", e.target.value)}
            />
          </div>

          {/* رفع الصورة */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              {t("uploadMediaFile")}
            </label>
            {!formData.imagePreview ? (
              <div
                className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-8 text-center cursor-pointer hover:border-blue-500 transition-colors"
                onClick={() => document.getElementById("media-upload")?.click()}
              >
                <Icon
                  name="Upload"
                  size={32}
                  className="text-gray-400 mx-auto mb-2"
                />
                <p className="text-sm text-gray-500">{t("dragDropOrClick")}</p>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="media-upload"
                />
              </div>
            ) : (
              <div className="relative border border-gray-300 dark:border-gray-600 rounded-lg p-2">
                <img
                  src={formData?.imagePreview?.url || formData.imagePreview}
                  alt="Preview"
                  className="w-full h-48 object-contain rounded bg-gray-50"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={removeMediaFile}
                  className="absolute top-2 right-2 bg-red-100 hover:bg-red-200 text-red-600 rounded-full"
                >
                  <Icon name="X" size={16} />
                </Button>
              </div>
            )}
            {errors?.image && (
              <p className="text-sm text-red-600 mt-1">{errors?.image}</p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end p-6 border-t border-gray-200 dark:border-gray-700 gap-3">
          <Button variant="outline" onClick={onClose}>
            {t("cancel")}
          </Button>
          <Button onClick={handleSave} loading={isLoadingAction}>
            {campaign ? t("updateCampaign") : t("launchCampaign")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CampaignModal;
